import React, { useState, useEffect } from 'react';
import { getBackendURL } from '../../utils/backendURL';
import axios from 'axios';
import { 
  Search, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  XCircle,
  Eye,
  RefreshCw,
  Filter,
  Download,
  Bug,
  Activity,
  Calendar,
  TrendingUp
} from 'lucide-react';

const ExecutionDebug = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statistics, setStatistics] = useState({});

  const statusIcons = {
    completed: CheckCircle,
    failed: XCircle,
    running: Clock,
    timeout: AlertCircle,
    cancelled: XCircle
  };

  const statusColors = {
    completed: 'text-green-600 bg-green-100',
    failed: 'text-red-600 bg-red-100', 
    running: 'text-blue-600 bg-blue-100',
    timeout: 'text-yellow-600 bg-yellow-100',
    cancelled: 'text-gray-600 bg-gray-100'
  };

  useEffect(() => {
    fetchExecutions();
    fetchStatistics();
  }, [statusFilter]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');

      const response = await axios.get(
        `${getBackendURL()}/api/ops/debug/executions?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setExecutions(response.data.executions);
    } catch (error) {
      console.error('Failed to fetch executions:', error);
      setError('Failed to load execution logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${getBackendURL()}/api/ops/debug/statistics`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchExecutionDetails = async (executionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${getBackendURL()}/api/ops/debug/executions/${executionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSelectedExecution(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Failed to fetch execution details:', error);
      alert('Failed to load execution details');
    }
  };

  const analyzeExecution = async (executionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${getBackendURL()}/api/ops/debug/executions/${executionId}/analyze`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Show analysis results
      alert(`Analysis Complete:\n\nCommon Issues: ${response.data.analysis_summary.common_error_patterns.join(', ')}\n\nSuggested Fixes:\n${response.data.analysis_summary.suggested_fixes.join('\n')}`);
      
    } catch (error) {
      console.error('Failed to analyze execution:', error);
      alert('Failed to analyze execution');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredExecutions = executions.filter(execution =>
    execution.test_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    execution.user_prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    execution.execution_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={fetchExecutions}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flow Test Execution Debug</h1>
          <p className="text-gray-600">Monitor and debug Flow Test executions with detailed logs</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={fetchExecutions}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Executions</p>
              <p className="text-2xl font-bold text-blue-900">{statistics.total_executions || 0}</p>
            </div>
            <Activity className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Success Rate (24h)</p>
              <p className="text-2xl font-bold text-green-900">
                {statistics.recent_24h?.success_rate?.toFixed(1) || 0}%
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Failed (24h)</p>
              <p className="text-2xl font-bold text-red-900">
                {statistics.recent_24h?.failed || 0}
              </p>
            </div>
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Recent (24h)</p>
              <p className="text-2xl font-bold text-gray-900">
                {statistics.recent_24h?.total || 0}
              </p>
            </div>
            <Calendar className="h-8 w-8 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by test name, prompt, or execution ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
            <option value="timeout">Timeout</option>
          </select>
        </div>
      </div>

      {/* Executions Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Execution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Results
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredExecutions.length > 0 ? filteredExecutions.map((execution) => {
                const StatusIcon = statusIcons[execution.status] || AlertCircle;
                
                return (
                  <tr key={execution.execution_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {execution.execution_id.slice(0, 8)}...
                        </div>
                        <div className="text-sm text-gray-500">
                          {execution.browser_type || 'chromium'} • {execution.device_type || 'desktop'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {execution.test_name || 'Unnamed Test'}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {execution.user_prompt || 'No prompt'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[execution.status] || 'text-gray-600 bg-gray-100'}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {execution.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        Errors: {execution.summary?.error_count || 0}
                      </div>
                      <div>
                        Success: {execution.summary?.success_rate?.toFixed(1) || 0}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(execution.created_at)}
                      {execution.summary?.duration_minutes && (
                        <div>({execution.summary.duration_minutes}m)</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => fetchExecutionDetails(execution.execution_id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      {execution.status === 'failed' && (
                        <button
                          onClick={() => analyzeExecution(execution.execution_id)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Analyze Errors"
                        >
                          <Bug className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No execution logs found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Execution Details Modal */}
      {showDetails && selectedExecution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <Bug className="h-6 w-6 text-blue-600" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Execution Details: {selectedExecution.execution.execution_id.slice(0, 12)}...
                  </h2>
                  <p className="text-sm text-gray-500">{selectedExecution.execution.test_name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Execution Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Execution Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Status:</span> {selectedExecution.execution.status}</div>
                  <div><span className="font-medium">Duration:</span> {selectedExecution.debug_info.duration_minutes} minutes</div>
                  <div><span className="font-medium">Browser:</span> {selectedExecution.execution.browser_type}</div>
                  <div><span className="font-medium">Device:</span> {selectedExecution.execution.device_type}</div>
                  <div><span className="font-medium">User Prompt:</span> {selectedExecution.execution.user_prompt}</div>
                  <div><span className="font-medium">Website:</span> {selectedExecution.execution.website_url}</div>
                </div>
              </div>

              {/* Debug Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Debug Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>Errors: {selectedExecution.debug_info.total_errors}</div>
                  <div>Browser Logs: {selectedExecution.debug_info.has_browser_logs ? 'Yes' : 'No'}</div>
                  <div>Network Logs: {selectedExecution.debug_info.has_network_logs ? 'Yes' : 'No'}</div>
                  <div>Screenshots: {selectedExecution.debug_info.has_screenshots ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Errors */}
              {selectedExecution.execution.errors && selectedExecution.execution.errors.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Errors ({selectedExecution.execution.errors.length})</h3>
                  <div className="space-y-3">
                    {selectedExecution.execution.errors.map((error, index) => (
                      <div key={index} className="border border-red-200 bg-red-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-red-800">{error.error_type}</div>
                            <div className="text-sm text-red-600 mt-1">{error.message}</div>
                            {error.step_name && (
                              <div className="text-xs text-red-500 mt-1">Step: {error.step_name}</div>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            error.severity === 'critical' ? 'bg-red-200 text-red-800' :
                            error.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                            'bg-yellow-200 text-yellow-800'
                          }`}>
                            {error.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Browser Console Logs */}
              {selectedExecution.execution.browser_console_logs && selectedExecution.execution.browser_console_logs.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Browser Console Logs</h3>
                  <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono max-h-60 overflow-y-auto">
                    {selectedExecution.execution.browser_console_logs.map((log, index) => (
                      <div key={index} className="mb-1">{log}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionDebug;